import { useState, useEffect, useRef, useCallback } from 'react';
import { Patient, Doctor, Item, CaseNoteHistory, DispenseHistory, VisitMemo, DrugAllergyItem, DrugAllergyDetail, DrugAllergyValidation, CodeSetItem, MemoType, SaveMemoRequest, SaveDispenseItem, SaveDrugAllergyRequest, PatientDetails, TeleVisitMeetStatus, PortalMode, HssSelection } from '../types/api';
import { OrderItem, AlertModalState, ConfirmModalState, DrugAllergyFormState, MemoFormState, TELE_CONSULT_ITEM_CODE, DEFAULT_DRUG_ALLERGY_FORM, DEFAULT_MEMO_FORM, DEFAULT_ALERT_MODAL, DEFAULT_CONFIRM_MODAL } from '../types/dispense';
import { apiService } from '../services/api';
import { normalizeLineBreaks, convertToWindowsLineBreaks } from '../utils/caseNote';
import { isConsultFinished } from '../utils/visitStage';
import { getApiErrorMessage } from '../utils/apiError';
import { visitUlidToApiGuid } from '../utils/guid';
import { getTodayISO, toDateOnlyISO } from '../utils/date';

interface UseDispenseDataProps {
  doctor: Doctor | null;
  patient: Patient;
  onBack: (notice?: string) => void;
  isExternalConsult?: boolean;
  portalMode?: PortalMode;
}

export function useDispenseData({ doctor, patient, onBack, isExternalConsult, portalMode = 'doctor' }: UseDispenseDataProps) {
  const isStaffPortal = portalMode === 'staff';
  const visitUlidRef = useRef(patient.visitULID);
  visitUlidRef.current = patient.visitULID;
  /** True after this session successfully called lockVisit (CheckLine: unlock on form close without save). */
  const visitLockedBySessionRef = useRef(false);
  /** Save dispense already unlocks server-side — skip unlock on navigate away. */
  const dispenseSavedRef = useRef(false);
  /** Finish consult after the diagnosis/save modal when dispense was still dirty. */
  const finishAfterSaveRef = useRef(false);

  const releaseVisitLock = useCallback(async () => {
    if (!visitLockedBySessionRef.current || dispenseSavedRef.current) {
      return;
    }
    visitLockedBySessionRef.current = false;
    try {
      await apiService.unlockVisit(visitUlidRef.current);
    } catch (error) {
      console.warn('Failed to unlock visit on exit:', error);
    }
  }, []);

  // Patient state
  const [patientDetails, setPatientDetails] = useState<Patient>(patient);
  const [patientDetailsLoading, setPatientDetailsLoading] = useState<boolean>(false);
  /** Google Meet for tele visits — filled after generate-consult-link when TeleMed. */
  const [teleConsultMeetUrl, setTeleConsultMeetUrl] = useState<string | null>(null);
  const [teleConsultMeetLoading, setTeleConsultMeetLoading] = useState<boolean>(false);
  const [teleConsultMeetError, setTeleConsultMeetError] = useState<string | null>(null);
  const [teleConsultMeetSkipped, setTeleConsultMeetSkipped] = useState<boolean>(false);

  /** Meet API v2 live room status (polled while tele visit is open). */
  const [meetLiveStatus, setMeetLiveStatus] = useState<TeleVisitMeetStatus | null>(null);

  // Form states
  const [caseNote, setCaseNote] = useState<string>('');
  const [savedCaseNote, setSavedCaseNote] = useState<string>('');
  const [caseNoteSaved, setCaseNoteSaved] = useState<boolean>(false);
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [ordersLoading, setOrdersLoading] = useState<boolean>(false);

  // Modal states
  const [showMCModal, setShowMCModal] = useState<boolean>(false);
  const [showItemModal, setShowItemModal] = useState<boolean>(false);
  const [showDiagnosisModal, setShowDiagnosisModal] = useState<boolean>(false);
  const [showOrderPanel, setShowOrderPanel] = useState<boolean>(false);
  const [editingOrder, setEditingOrder] = useState<OrderItem | null>(null);
  const [isAddingNewItem, setIsAddingNewItem] = useState<boolean>(false);
  const [showDrugAllergyModal, setShowDrugAllergyModal] = useState<boolean>(false);
  const [showDrugAllergyDetailModal, setShowDrugAllergyDetailModal] = useState<boolean>(false);

  // Drug allergy states
  const [drugAllergyValidation, setDrugAllergyValidation] = useState<DrugAllergyValidation | null>(null);
  const [drugAllergyDetails, setDrugAllergyDetails] = useState<DrugAllergyDetail[]>([]);
  const [drugAllergyLoading, setDrugAllergyLoading] = useState<boolean>(false);
  const [drugList, setDrugList] = useState<DrugAllergyItem[]>([]);
  const [selectedDrug, setSelectedDrug] = useState<DrugAllergyItem | null>(null);
  const [drugSearchTerm, setDrugSearchTerm] = useState<string>('');
  const [editingDrugAllergy, setEditingDrugAllergy] = useState<DrugAllergyDetail | null>(null);
  const [isAddingNewDrugAllergy, setIsAddingNewDrugAllergy] = useState<boolean>(false);

  // Code Set states
  const [drugProbabilityList, setDrugProbabilityList] = useState<CodeSetItem[]>([]);
  const [informationSourceList, setInformationSourceList] = useState<CodeSetItem[]>([]);
  const [drugAllergyIndicatorList, setDrugAllergyIndicatorList] = useState<CodeSetItem[]>([]);
  const [alertIndicatorList, setAlertIndicatorList] = useState<CodeSetItem[]>([]);
  const [reactionOutcomeList, setReactionOutcomeList] = useState<CodeSetItem[]>([]);
  const [adverseReactionList, setAdverseReactionList] = useState<CodeSetItem[]>([]);

  // Drug allergy form
  const [drugAllergyForm, setDrugAllergyForm] = useState<DrugAllergyFormState>(DEFAULT_DRUG_ALLERGY_FORM);

  // Alert/confirm modals
  const [alertModal, setAlertModal] = useState<AlertModalState>(DEFAULT_ALERT_MODAL);
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>(DEFAULT_CONFIRM_MODAL);

  // Check if visit is checked out
  const isCheckedOut = patient.visitStage === 60;

  // MC form
  const [mcDays, setMcDays] = useState<number>(1);
  const [mcFromDate, setMcFromDate] = useState<string>(getTodayISO());
  const [mcDateError, setMcDateError] = useState<string>('');
  const [mcRemarks, setMcRemarks] = useState<string>('');
  const [editingMC, setEditingMC] = useState<any | null>(null);

  // Item search
  const [itemSearch, setItemSearch] = useState<string>('');
  const [itemFilter, setItemFilter] = useState<string>('ALL');
  const itemFilterRef = useRef<string>('ALL');
  const [items, setItems] = useState<Item[]>([]);
  const [itemsLoading, setItemsLoading] = useState<boolean>(false);

  // Keep ref in sync with state
  useEffect(() => {
    itemFilterRef.current = itemFilter;
  }, [itemFilter]);

  // History data
  const [caseNoteHistory, setCaseNoteHistory] = useState<CaseNoteHistory[]>([]);
  const [dispenseHistory, setDispenseHistory] = useState<DispenseHistory[]>([]);
  const [mcHistory, setMcHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);

  // Memo state
  const [memos, setMemos] = useState<VisitMemo[]>([]);
  const [memoLoading, setMemoLoading] = useState<boolean>(false);
  const [showMemoModal, setShowMemoModal] = useState<boolean>(false);
  const [showMemoTypeModal, setShowMemoTypeModal] = useState<boolean>(false);
  const [editingMemo, setEditingMemo] = useState<VisitMemo | null>(null);
  const [selectedMemoType, setSelectedMemoType] = useState<MemoType | null>(null);
  const [memoForm, setMemoForm] = useState<MemoFormState>(DEFAULT_MEMO_FORM);

  // Diagnosis selection
  const [selectedAcute, setSelectedAcute] = useState<string[]>([]);
  const [selectedChronic, setSelectedChronic] = useState<string[]>([]);
  // HSS (Haze Subsidy Scheme): doctor's Haze choice. Null = server default (apply when eligible).
  const [hssSelection, setHssSelection] = useState<HssSelection | null>(null);
  const [diagnosisSearch, setDiagnosisSearch] = useState<string>('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [diagnosisTab, setDiagnosisTab] = useState<'common' | 'all' | 'chronic'>('common');

  // Sidebar
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  // ============================================================
  // Data Loading Functions
  // ============================================================

  const loadPatientDetails = async (): Promise<PatientDetails | null> => {
    try {
      setPatientDetailsLoading(true);
      const details = await apiService.getPatientDetails(patient.visitULID);
      setPatientDetails({
        ...patient,
        name: details.fullName,
        nric: details.nric,
        age: details.age,
        gender: (details.gender === 'M' || details.gender === 'F') ? details.gender : 'M' as 'M' | 'F',
        store: details.storeName,
        storeId: details.storeID,
        temp: details.temperature ?? 0,
        sbp: details.sbp ?? 0,
        dbp: details.dbp ?? 0,
        pulse: details.pulse ?? 0,
        spo2: details.spo2 ?? 0,
        weight: details.weight ?? 0,
        height: details.height ?? 0,
        waist: details.waist ?? 0,
        remarks: details.remarks || '',
        allergies: details.allergies || [],
        chronicConditions: details.chronicConditions || [],
        chronicConditionCodes: details.chronicConditionCodes || [],
        currentVisitAcuteDiagnosisCodes: details.currentVisitAcuteDiagnosisCodes || [],
        currentVisitChronicDiagnosisCodes: details.currentVisitChronicDiagnosisCodes || [],
        empId: details.empId,
        walkIn: details.walkIn,
        teleMed: details.teleMed,
        newPt: details.newPt ?? patient.newPt,
        corporateCompanyName: details.corporateCompanyName,
        triageSummary: details.triageSummary ?? patient.triageSummary ?? undefined,
        visitStage: details.visitStage ?? patient.visitStage,
        consultFinalized: details.consultFinalized ?? patient.consultFinalized,
        autoCheckoutEnabled: details.autoCheckoutEnabled === true,
        chas: details.chas ?? null,
        hss: details.hss ?? null,
      });
      if (details.customerULID) {
        await loadDrugAllergyValidation();
      }
      return details;
    } catch (error) {
      console.error('Error loading patient details:', error);
      return null;
    } finally {
      setPatientDetailsLoading(false);
    }
  };

  const loadDrugAllergyValidation = async () => {
    try {
      const details = await apiService.getPatientDetails(patient.visitULID);
      if (details?.customerULID) {
        const validation = await apiService.getDrugAllergyValidation(details.customerULID);
        setDrugAllergyValidation(validation);
      }
    } catch (error) {
      console.error('Error loading drug allergy validation:', error);
      setDrugAllergyValidation({ hasDrugAllergyRecord: true });
    }
  };

  const loadCurrentOrders = async () => {
    try {
      setOrdersLoading(true);
      const currentOrders = await apiService.getCurrentOrders(patient.visitULID);
      const orderItems: OrderItem[] = currentOrders.map(order => {
        const itemType: 'MED' | 'PRC' | 'LAB' =
          order.itemType === 0 ? 'MED' :
          order.itemType === 9 ? 'LAB' :
          (order.itemType === 5 || order.itemType === 6) ? 'PRC' : 'PRC';
        return {
          id: order.itemID,
          code: order.itemCode,
          name: order.itemName,
          publishName: order.publishName,
          price: order.unitPrice,
          type: itemType,
          defaultQty: order.quantity,
          quantity: order.quantity,
          unitPrice: order.unitPrice,
          qty: order.quantity,
          dosage: order.dosage,
          doseUOM: order.doseUOM,
          dispenseUOM: order.dispenseUOM,
          frequency: order.frequency,
          instruction: order.instruction,
          precaution: order.precaution,
          purpose: order.purpose
        };
      });
      setOrders(orderItems);
    } catch (error: any) {
      console.error('Error loading current orders:', error);
      if (error.response?.status === 409 || error.response?.data?.errorCode === 'VISIT_LOCKED') {
        setAlertModal({ show: true, message: 'This visit is currently being edited by another user. Please wait or contact them.', type: 'error' });
        setTimeout(() => { onBack(); }, 2000);
        return;
      }
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadCaseNoteHistory = async () => {
    try {
      setHistoryLoading(true);
      const history = await apiService.getCaseNoteHistory(patient.visitULID);
      setCaseNoteHistory(history);
    } catch (error) {
      console.error('Error loading case note history:', error);
      setCaseNoteHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadDispenseHistory = async () => {
    try {
      setHistoryLoading(true);
      const history = await apiService.getDispenseHistory(patient.visitULID);
      setDispenseHistory(history);
    } catch (error) {
      console.error('Error loading dispense history:', error);
      setDispenseHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadCurrentCaseNote = async () => {
    try {
      const response = await apiService.getCurrentCaseNote(patient.visitULID);
      if (response.caseNote) {
        const normalizedCaseNote = normalizeLineBreaks(response.caseNote);
        setCaseNote(normalizedCaseNote);
        setSavedCaseNote(normalizedCaseNote);
      }
    } catch (error) {
      console.error('Error loading current case note:', error);
    }
  };

  const loadMemos = async () => {
    try {
      setMemoLoading(true);
      const details = await apiService.getPatientDetails(patient.visitULID);
      if (!details?.customerULID) {
        setAlertModal({ show: true, message: 'Unable to get customer information to load memos', type: 'error' });
        setMemos([]);
        return;
      }
      const memoList = await apiService.getMemosByCustomer(details.customerULID);
      setMemos(memoList || []);
    } catch (error: any) {
      console.error('Error loading memos:', error);
      setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not load memos. Please try again.'), type: 'error' });
      setMemos([]);
    } finally {
      setMemoLoading(false);
    }
  };

  const loadMCHistory = async () => {
    try {
      setHistoryLoading(true);
      const history = await apiService.getMCHistory(patient.visitULID);
      console.log('=== MC History Debug ===');
      console.log('Patient visitULID:', patient.visitULID, typeof patient.visitULID);
      if (history.length > 0) {
        console.log('MC History count:', history.length);
        history.forEach((mc, index) => {
          const mcVisitULID = (mc as any).visitULID || (mc as any).VisitULID;
          console.log(`MC ${index}:`, {
            ulid: mc.ulid,
            visitULID: mcVisitULID,
            visitULIDType: typeof mcVisitULID,
            allKeys: Object.keys(mc),
            matches: String(mcVisitULID || '').toLowerCase() === String(patient.visitULID || '').toLowerCase()
          });
        });
      } else {
        console.log('No MC history found');
      }
      setMcHistory(history);
    } catch (error) {
      console.error('Error loading MC history:', error);
      setMcHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // ============================================================
  // Drug Allergy Functions
  // ============================================================

  const loadDrugList = async (searchTerm?: string) => {
    try {
      const drugs = await apiService.getDrugList(searchTerm);
      setDrugList(drugs);
    } catch (error) {
      console.error('Error loading drug list:', error);
      setDrugList([]);
    }
  };

  const drugSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleDrugSearchChange = async (value: string) => {
    setDrugSearchTerm(value);
    if (drugSearchTimeoutRef.current) {
      clearTimeout(drugSearchTimeoutRef.current);
    }
    drugSearchTimeoutRef.current = setTimeout(async () => {
      if (value.length >= 2 || value.length === 0) {
        await loadDrugList(value || undefined);
      }
    }, 300);
  };

  const loadDrugAllergyDetails = async () => {
    try {
      setDrugAllergyLoading(true);
      const details = await apiService.getPatientDetails(patient.visitULID);
      if (details?.customerULID) {
        const allergyDetails = await apiService.getDrugAllergyDetails(details.customerULID);
        setDrugAllergyDetails(allergyDetails);
      }
    } catch (error) {
      console.error('Error loading drug allergy details:', error);
      setDrugAllergyDetails([]);
    } finally {
      setDrugAllergyLoading(false);
    }
  };

  const resetDrugAllergyForm = () => {
    setDrugAllergyForm(DEFAULT_DRUG_ALLERGY_FORM);
  };

  const handleOpenDrugAllergyModal = async () => {
    setShowDrugAllergyModal(true);
    await loadDrugList();
    await loadDrugAllergyDetails();
    await loadCodeSets();
    setSelectedDrug(null);
    setEditingDrugAllergy(null);
    setIsAddingNewDrugAllergy(false);
    setDrugSearchTerm('');
    resetDrugAllergyForm();
  };

  const handleSelectDrug = async (drug: DrugAllergyItem) => {
    setSelectedDrug(drug);
    setIsAddingNewDrugAllergy(true);
    setEditingDrugAllergy(null);
    resetDrugAllergyForm();
    if (drugProbabilityList.length === 0) {
      await loadCodeSets();
    }
    setShowDrugAllergyDetailModal(true);
  };

  const handleEditDrugAllergy = async (allergy: DrugAllergyDetail) => {
    const drugItem = drugList.find(d => d.code === allergy.drugCode);
    if (drugItem) {
      setSelectedDrug(drugItem);
    } else {
      setSelectedDrug({ code: allergy.drugCode, description: allergy.drugName });
    }
    setEditingDrugAllergy(allergy);
    setIsAddingNewDrugAllergy(false);
    setDrugAllergyForm({
      drugProbabilityCode: allergy.drugProbabilityCode || '1',
      reactionCode: allergy.reactionCode || '0',
      outcomeCode: allergy.outcomeCode || '6',
      informationSourceCode: allergy.informationSourceCode || '03',
      drugAllergyIndicatorCode: allergy.drugAllergyIndicatorCode || 'Y',
      alertIndicatorCode: allergy.alertIndicatorCode || '2'
    });
    if (drugProbabilityList.length === 0) {
      await loadCodeSets();
    }
    setShowDrugAllergyDetailModal(true);
  };

  const loadCodeSets = async () => {
    try {
      const [drugProbability, informationSource, drugAllergyIndicator, alertIndicator, reactionOutcome, adverseReaction] = await Promise.all([
        apiService.getDrugProbabilityList(),
        apiService.getInformationSourceList(),
        apiService.getDrugAllergyIndicatorList(),
        apiService.getAlertIndicatorList(),
        apiService.getReactionOutcomeList(),
        apiService.getAdverseReactionList()
      ]);
      setDrugProbabilityList(drugProbability);
      setInformationSourceList(informationSource);
      setDrugAllergyIndicatorList(drugAllergyIndicator);
      setAlertIndicatorList(alertIndicator);
      setReactionOutcomeList(reactionOutcome);
      setAdverseReactionList(adverseReaction);
    } catch (error) {
      console.error('Error loading code sets:', error);
    }
  };

  const handleSaveDrugAllergy = async () => {
    if (!selectedDrug) {
      setAlertModal({ show: true, message: 'Please select a drug', type: 'error' });
      return;
    }
    try {
      const details = await apiService.getPatientDetails(patient.visitULID);
      if (!details?.customerULID) {
        setAlertModal({ show: true, message: 'Unable to get customer information', type: 'error' });
        return;
      }
      if (editingDrugAllergy && !isAddingNewDrugAllergy) {
        await apiService.removeDrugAllergy(details.customerULID, editingDrugAllergy.drugCode);
      }
      const request: SaveDrugAllergyRequest = {
        customerULID: details.customerULID,
        drugCode: selectedDrug.code,
        drugProbabilityCode: drugAllergyForm.drugProbabilityCode,
        reactionCode: drugAllergyForm.reactionCode,
        outcomeCode: drugAllergyForm.outcomeCode,
        informationSourceCode: drugAllergyForm.informationSourceCode,
        drugAllergyIndicatorCode: drugAllergyForm.drugAllergyIndicatorCode,
        alertIndicatorCode: drugAllergyForm.alertIndicatorCode
      };
      const response = await apiService.saveDrugAllergy(details.customerULID, request);
      if (response.success) {
        setShowDrugAllergyDetailModal(false);
        setSelectedDrug(null);
        setEditingDrugAllergy(null);
        setIsAddingNewDrugAllergy(false);
        setDrugSearchTerm('');
        resetDrugAllergyForm();
        setAlertModal({ show: true, message: isAddingNewDrugAllergy ? 'Drug allergy saved successfully' : 'Drug allergy updated successfully', type: 'success' });
        await loadDrugAllergyDetails();
        await loadPatientDetails();
        await loadDrugAllergyValidation();
      } else {
        setAlertModal({ show: true, message: response.message || 'Failed to save drug allergy', type: 'error' });
      }
    } catch (error: any) {
      console.error('Error saving drug allergy:', error);
      setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not save drug allergy. Please try again.'), type: 'error' });
    }
  };

  const handleSaveNoDrugAllergy = async () => {
    try {
      const details = await apiService.getPatientDetails(patient.visitULID);
      if (!details?.customerULID) {
        setAlertModal({ show: true, message: 'Unable to get customer information', type: 'error' });
        return;
      }
      const response = await apiService.saveNoDrugAllergy(details.customerULID);
      if (response.success) {
        setAlertModal({ show: true, message: 'No known drug allergy saved successfully', type: 'success' });
        await loadDrugAllergyDetails();
        await loadPatientDetails();
        await loadDrugAllergyValidation();
      } else {
        setAlertModal({ show: true, message: response.message || 'Failed to save no drug allergy', type: 'error' });
      }
    } catch (error: any) {
      console.error('Error saving no drug allergy:', error);
      setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not update allergy record. Please try again.'), type: 'error' });
    }
  };

  const handleRemoveDrugAllergy = async (drugCode: string) => {
    setConfirmModal({
      show: true,
      message: 'Are you sure you want to remove this drug allergy?',
      onConfirm: async () => {
        try {
          const details = await apiService.getPatientDetails(patient.visitULID);
          if (!details?.customerULID) {
            setAlertModal({ show: true, message: 'Unable to get customer information', type: 'error' });
            setConfirmModal(DEFAULT_CONFIRM_MODAL);
            return;
          }
          const response = await apiService.removeDrugAllergy(details.customerULID, drugCode);
          if (response.success) {
            setAlertModal({ show: true, message: 'Drug allergy removed successfully', type: 'success' });
            await loadDrugAllergyDetails();
            await loadPatientDetails();
            await loadDrugAllergyValidation();
          } else {
            setAlertModal({ show: true, message: response.message || 'Failed to remove drug allergy', type: 'error' });
          }
        } catch (error: any) {
          console.error('Error removing drug allergy:', error);
          setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not remove drug allergy. Please try again.'), type: 'error' });
        }
        setConfirmModal(DEFAULT_CONFIRM_MODAL);
      },
      onCancel: () => { setConfirmModal(DEFAULT_CONFIRM_MODAL); }
    });
  };

  // ============================================================
  // Item / Order Functions
  // ============================================================

  const searchItems = async (searchTerm: string = '', filterOverride?: string) => {
    if (!patientDetails.storeId) {
      console.warn('Cannot search items: patient.storeId is missing');
      setAlertModal({ show: true, message: 'Store information is missing. Cannot load items.', type: 'error' });
      return;
    }
    const activeFilter = filterOverride !== undefined ? filterOverride : itemFilterRef.current;
    try {
      setItemsLoading(true);
      console.log('Searching items:', { searchTerm, itemFilter: activeFilter, storeId: patientDetails.storeId, note: 'Backend should filter: inactive=0, discontinued=0, storeID=' + patientDetails.storeId });
      const fetchedItems = await apiService.searchItems(searchTerm, activeFilter, patientDetails.storeId);
      console.log('Fetched items:', fetchedItems.length);
      const activeItems = fetchedItems.filter(item => true); // Trust backend filtering
      setItems(activeItems);
    } catch (error: any) {
      console.error('Error loading items:', error);
      console.error('Error details:', error.response?.data || error.message);
      setItems([]);
      setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not load items. Please try again.'), type: 'error' });
    } finally {
      setItemsLoading(false);
    }
  };

  const filteredItems = items.filter(item => {
    const matchSearch = itemSearch === '' ||
      item.itemName.toLowerCase().includes(itemSearch.toLowerCase()) ||
      item.publishName.toLowerCase().includes(itemSearch.toLowerCase()) ||
      item.itemCode.toLowerCase().includes(itemSearch.toLowerCase());
    const matchFilter = itemFilter === 'ALL' || item.itemType === itemFilter;
    return matchSearch && matchFilter;
  });

  const openItemEditModal = (item: Item) => {
    let defaultDosage: number = 0;
    if (item.defaultDose) {
      const doseStr = item.defaultDose.trim();
      const match = doseStr.match(/^(\d+(?:\.\d+)?)/);
      if (match) {
        defaultDosage = parseFloat(match[1]);
      }
    }
    const defaultQuantity = item.defaultDispenseQty && item.defaultDispenseQty > 0 ? item.defaultDispenseQty : 1;
    const defaultPrice = item.price || 0;
    const newOrder: OrderItem = {
      id: item.id,
      code: item.itemCode,
      name: item.itemName,
      publishName: item.publishName,
      price: defaultPrice,
      type: item.itemType,
      defaultQty: defaultQuantity,
      defaultDose: item.defaultDose,
      freq: item.defaultFrequency,
      quantity: defaultQuantity,
      unitPrice: defaultPrice,
      dosage: defaultDosage,
      doseUOM: item.uom_Dose || (item.itemType === 'MED' ? 'tablet' : undefined),
      dispenseUOM: item.uOM_Disp || (item.itemType === 'MED' ? 'tablet' : 'unit'),
      frequency: item.defaultFrequency || '',
      instruction: item.defaultInstruction || '',
      precaution: item.defaultPrecaution || '',
      purpose: item.defaultPurpose || '',
      isCHASClaimable: item.isCHASClaimable || false,
      isMedisaveClaimable: item.isMedisaveClaimable || false,
      isCDMPClaimable: item.isCDMPClaimable || false,
      isManagedCare: item.isManagedCare || false,
      qty: defaultQuantity,
      isChronic: false
    };
    setEditingOrder(newOrder);
    setIsAddingNewItem(true);
    setShowItemModal(false);
  };

  const addItem = (item: Item) => {
    if (!(patientDetails.empId != null && patientDetails.empId > 0)) {
      setAlertModal({ show: true, message: 'Please pick the patient first before adding items.', type: 'error' });
      return;
    }
    const exists = orders.find(o => o.id === item.id);
    if (exists) {
      editOrder(exists);
      setShowItemModal(false);
      return;
    }
    if (patientDetails.allergies.some(a => item.itemName.toLowerCase().includes(a.toLowerCase()))) {
      setConfirmModal({
        show: true,
        message: `WARNING: Patient is allergic to ${patientDetails.allergies.join(', ')}!\n\nAre you sure you want to add ${item.itemName}?`,
        confirmText: 'Add Anyway',
        confirmStyle: 'btn-danger',
        onConfirm: () => { setConfirmModal(DEFAULT_CONFIRM_MODAL); openItemEditModal(item); },
        onCancel: () => { setConfirmModal(DEFAULT_CONFIRM_MODAL); }
      });
      return;
    }
    openItemEditModal(item);
  };

  const removeItem = async (id: number) => {
    const itemToRemove = orders.find(o => o.id === id);
    if (!itemToRemove) { console.warn('Item not found in orders:', id); return; }
    if (itemToRemove.code === TELE_CONSULT_ITEM_CODE) return;

    setOrders(prevOrders => prevOrders.filter(o => o.id !== id));
    try {
      const success = await apiService.removeOrderItem(patient.visitULID, id);
      if (success) {
        console.log('Item removed from VisitItemEntry successfully');
        await loadCurrentOrders();
      } else {
        setOrders(prevOrders => [...prevOrders, itemToRemove]);
        setAlertModal({ show: true, message: 'Failed to remove item. It may be locked or not found in the database.', type: 'error' });
      }
    } catch (error: any) {
      console.error('Error removing item from VisitItemEntry:', error);
      setOrders(prevOrders => [...prevOrders, itemToRemove]);
      setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not remove item. It has been restored on screen.'), type: 'error' });
    }
  };

  const editOrder = (order: OrderItem) => {
    if (order.code === TELE_CONSULT_ITEM_CODE) return;
    setEditingOrder({ ...order });
    setIsAddingNewItem(false);
  };

  const saveEditedOrder = async () => {
    if (!editingOrder) return;
    if (!(patientDetails.empId != null && patientDetails.empId > 0)) {
      setAlertModal({ show: true, message: 'Please pick the patient first before saving items.', type: 'error' });
      return;
    }

    const saveItem: SaveDispenseItem = {
      itemID: editingOrder.id,
      itemCode: editingOrder.code,
      itemName: editingOrder.name,
      publishName: editingOrder.publishName,
      quantity: editingOrder.quantity || editingOrder.qty || 1,
      unitPrice: editingOrder.unitPrice || editingOrder.price,
      dosage: editingOrder.dosage,
      doseUOM: editingOrder.doseUOM,
      dispenseUOM: editingOrder.dispenseUOM,
      frequency: editingOrder.frequency,
      instruction: editingOrder.instruction,
      purpose: editingOrder.purpose,
      precaution: editingOrder.precaution,
      isCHASClaimable: editingOrder.isCHASClaimable || false,
      isMedisaveClaimable: editingOrder.isMedisaveClaimable || false,
      isCDMPClaimable: editingOrder.isCDMPClaimable || false,
      isManagedCare: editingOrder.isManagedCare || false,
      itemType: editingOrder.type === 'MED' ? 0 : editingOrder.type === 'LAB' ? 9 : 5
    };

    if (isAddingNewItem) {
      try {
        await apiService.addOrderItem(patient.visitULID, saveItem);
        await loadCurrentOrders();
      } catch (error: any) {
        console.error('Error adding item to VisitItemEntry:', error);
        setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not save item. Please try again.'), type: 'error' });
        return;
      }
    } else {
      try {
        const removeSuccess = await apiService.removeOrderItem(patient.visitULID, editingOrder.id);
        if (!removeSuccess) {
          setAlertModal({ show: true, message: 'Failed to update item. It may be locked or not found in the database.', type: 'error' });
          return;
        }
        await apiService.addOrderItem(patient.visitULID, saveItem);
        await loadCurrentOrders();
      } catch (error: any) {
        console.error('Error updating item in VisitItemEntry:', error);
        setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not update item. Please try again.'), type: 'error' });
        return;
      }
    }
    setEditingOrder(null);
    setIsAddingNewItem(false);
  };

  const totalAmount = orders.reduce((sum, o) => sum + ((o.unitPrice || o.price) * (o.quantity || o.qty || 1)), 0);

  // ============================================================
  // Diagnosis Functions
  // ============================================================

  const toggleAcute = (code: string) => {
    const hasVaccine = orders.some(o => o.code?.startsWith('VAC') || o.code?.startsWith('VACC'));
    if (code === 'Z269' && hasVaccine && selectedAcute.includes(code)) {
      setAlertModal({ show: true, message: 'Patient has Vaccination items - Cannot de-select Immunization Diagnosis!', type: 'error' });
      return;
    }
    const hasCDS = orders.some(o => o.code === 'P-CDS');
    if (code === 'Z001' && hasCDS && selectedAcute.includes(code)) {
      setAlertModal({ show: true, message: 'Patient has Child Developmental Screening Charged - Cannot de-select CDS Diagnosis!', type: 'error' });
      return;
    }
    setSelectedAcute(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const toggleChronic = (code: string) => {
    setSelectedChronic(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const toggleCategory = (label: string) => {
    setExpandedCategories(prev => prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]);
  };

  const filterDiagnoses = (items: { code: string; name: string }[]) => {
    if (!diagnosisSearch.trim()) return items;
    const search = diagnosisSearch.toLowerCase();
    return items.filter(d => d.name.toLowerCase().includes(search) || d.code.toLowerCase().includes(search));
  };

  // ============================================================
  // Pick / Unpick Functions
  // ============================================================

  const getPickButtonState = () => {
    const visitEmpId = patientDetails.empId;
    const currentEmpId = doctor?.id;
    if (!visitEmpId || visitEmpId === 0) {
      return { text: 'Pick', color: 'default', enabled: true, action: 'pick' };
    }
    if (visitEmpId === currentEmpId) {
      if (isConsultFinished(patientDetails.visitStage)) {
        return {
          text: 'Consult complete',
          color: 'grey',
          enabled: false,
          action: 'none',
          title: 'Consult finished — unpick is no longer available. Proceed to Julius for checkout.',
        };
      }
      return { text: 'Cancel', color: 'red', enabled: true, action: 'unpick', title: 'Cancel Pick (Unpick Patient)' };
    }
    return { text: 'Picked', color: 'grey', enabled: false, action: 'none', title: 'Patient picked by another user' };
  };

  const handlePickButtonClick = async () => {
    const buttonState = getPickButtonState();
    if (buttonState.action === 'pick') {
      try {
        await apiService.pickVisit(patient.visitULID);
        await loadPatientDetails();
        await loadCurrentOrders();
        setAlertModal({ show: true, message: 'Patient picked successfully!', type: 'success' });
      } catch (error: unknown) {
        console.error('Error picking patient:', error);
        setAlertModal({
          show: true,
          message: getApiErrorMessage(
            error,
            'Could not pick this patient. The visit may already be assigned to another doctor.',
          ),
          type: 'error',
        });
      }
    } else if (buttonState.action === 'unpick') {
      try {
        const unpickResult = await apiService.unpickVisit(patient.visitULID);
        if (!unpickResult) {
          setAlertModal({ show: true, message: 'Failed to unpick patient. You can only unpick visits that you picked.', type: 'error' });
          return;
        }
        await loadPatientDetails();
        setAlertModal({ show: true, message: 'Patient unpicked successfully!', type: 'success' });
      } catch (error: any) {
        console.error('Error unpicking patient:', error);
        setAlertModal({ show: true, message: getApiErrorMessage(error, 'Could not unpick this patient. Please try again.'), type: 'error' });
      }
    }
  };

  // ============================================================
  // Save Functions
  // ============================================================

  const handleSaveClick = async () => {
    if (!(patientDetails.empId != null && patientDetails.empId > 0)) {
      setAlertModal({ show: true, message: 'Please pick the patient first before saving.', type: 'error' });
      return;
    }
    try {
      const details = await apiService.getPatientDetails(patient.visitULID);
      if (details?.customerULID) {
        const validation = await apiService.getDrugAllergyValidation(details.customerULID);
        setDrugAllergyValidation(validation);
        if (!validation.hasDrugAllergyRecord) {
          setAlertModal({ show: true, message: 'Please record drug allergy information (either select a drug allergy or mark as "No Known Drug Allergy") before saving the dispense.', type: 'error' });
          return;
        }
      }
    } catch (error) {
      console.error('Error checking drug allergy validation:', error);
    }
    setDiagnosisSearch('');
    setDiagnosisTab('common');
    setExpandedCategories([]);
    const existingAcute = patientDetails.currentVisitAcuteDiagnosisCodes || [];
    const existingChronic = patientDetails.currentVisitChronicDiagnosisCodes || [];
    const initialAcute = [...existingAcute];
    const initialChronic = [...existingChronic];
    const hasVaccine = orders.some(o => o.code?.startsWith('VAC') || o.code?.startsWith('VACC'));
    if (hasVaccine && !initialAcute.includes('Z269')) { initialAcute.push('Z269'); }
    const hasCDS = orders.some(o => o.code === 'P-CDS');
    if (hasCDS && !initialAcute.includes('Z001')) { initialAcute.push('Z001'); }
    setSelectedAcute(initialAcute);
    setSelectedChronic(initialChronic);
    setShowDiagnosisModal(true);
  };

  const runFinishConsult = async () => {
    const isCorporate = Boolean(patientDetails.corporateCompanyName?.trim());
    try {
      const response = await apiService.finishConsult(patient.visitULID, hssSelection);
      if (response.success) {
        setPatientDetails((prev) => ({ ...prev, consultFinalized: true }));
        const notice =
          response.message ||
          (isCorporate
            ? 'Consult finished. The consultation fee is billed to the company. If there is a memo or medication, the partner sends the patient a Stripe link for those charges.'
            : 'Consult finished. The patient will complete pickup, delivery, or extra payment on the portal.');
        void releaseVisitLock().finally(() => {
          onBack(notice);
        });
      } else {
        setAlertModal({
          show: true,
          message: getApiErrorMessage({ response: { data: { message: response.message } } }, 'Could not finish consult.'),
          type: 'error',
        });
      }
    } catch (error: unknown) {
      console.error('Error finishing consult:', error);
      setAlertModal({
        show: true,
        message: getApiErrorMessage(error, 'Could not finish consult. Please try again.'),
        type: 'error',
      });
    }
  };

  const handleFinishConsult = async () => {
    if (isExternalConsult || isStaffPortal) return;
    if (!(patientDetails.empId != null && patientDetails.empId > 0)) {
      setAlertModal({ show: true, message: 'Please pick the patient first before finishing the consult.', type: 'error' });
      return;
    }
    if (!isConsultFinished(patientDetails.visitStage)) {
      finishAfterSaveRef.current = true;
      await handleSaveClick();
      return;
    }
    setConfirmModal({
      show: true,
      message: Boolean(patientDetails.corporateCompanyName?.trim())
        ? 'Finish this consult? The consultation fee is billed to the company. If there is a memo or medication, the partner sends the patient a Stripe link for those charges. You cannot add more medications after this.'
        : 'Finish this consult? The patient will complete pickup or delivery and any extra payment on the portal. You cannot add more medications after this.',
      confirmText: 'Finish consult',
      onConfirm: () => {
        setConfirmModal(DEFAULT_CONFIRM_MODAL);
        void runFinishConsult();
      },
      onCancel: () => setConfirmModal(DEFAULT_CONFIRM_MODAL),
    });
  };

  const handleConfirmSave = async () => {
    if (selectedAcute.length === 0 && selectedChronic.length === 0) {
      setAlertModal({ show: true, message: 'Please select at least one diagnosis.', type: 'error' });
      return;
    }
    try {
      setShowDiagnosisModal(false);
      const caseNoteToSave = convertToWindowsLineBreaks(savedCaseNote || caseNote);
      const saveRequest = {
        visitULID: patient.visitULID,
        caseNote: caseNoteToSave,
        items: orders.map(order => ({
          itemID: order.id,
          itemCode: order.code,
          itemName: order.name,
          publishName: order.publishName,
          quantity: order.quantity || order.qty || 1,
          unitPrice: order.unitPrice || order.price,
          dosage: order.dosage,
          doseUOM: order.doseUOM,
          dispenseUOM: order.dispenseUOM,
          frequency: order.frequency,
          instruction: order.instruction,
          purpose: order.purpose,
          precaution: order.precaution,
          isCHASClaimable: order.isCHASClaimable || false,
          isMedisaveClaimable: order.isMedisaveClaimable || false,
          isCDMPClaimable: order.isCDMPClaimable || false,
          isManagedCare: order.isManagedCare || false,
          itemType: order.type === 'MED' ? 0 : order.type === 'LAB' ? 9 : 5
        })),
        acuteDiagnosisCodes: selectedAcute,
        chronicDiagnosisCodes: selectedChronic,
        hss: hssSelection,
      };
      const response = await apiService.saveDispense(patient.visitULID, saveRequest);
      if (response.success) {
        dispenseSavedRef.current = true;
        visitLockedBySessionRef.current = false;
        setPatientDetails((prev) => ({ ...prev, visitStage: Math.max(prev.visitStage ?? 0, 30) }));
        const autoCheckout =
          Boolean(patientDetails.autoCheckoutEnabled) &&
          Boolean(patientDetails.teleMed) &&
          patientDetails.walkIn !== true;
        if (finishAfterSaveRef.current || autoCheckout) {
          finishAfterSaveRef.current = false;
          await runFinishConsult();
          return;
        }
        if (isExternalConsult) {
          setAlertModal({ show: true, message: 'Consult saved successfully!\n\nThis window will close automatically.', type: 'success' });
          setTimeout(() => { onBack(); }, 2000);
        } else {
          setAlertModal({ show: true, message: 'Dispense saved successfully!\n\nProceed to Julius for CHAS/Medisave claims and payment.', type: 'success' });
          setTimeout(() => { onBack(); }, 2000);
        }
      } else {
        setAlertModal({
          show: true,
          message: getApiErrorMessage(
            { response: { data: { message: response.message } } },
            'Could not save dispense. Please try again.',
          ),
          type: 'error',
        });
      }
    } catch (error: unknown) {
      console.error('Error saving dispense:', error);
      setAlertModal({
        show: true,
        message: getApiErrorMessage(error, 'Could not save dispense. Please try again.'),
        type: 'error',
      });
    }
  };

  const handleBack = useCallback(() => {
    void releaseVisitLock().finally(() => {
      onBack();
    });
  }, [onBack, releaseVisitLock]);

  // ============================================================
  // Effects
  // ============================================================

  // Unlock when the user leaves without saving (browser tab close / refresh).
  useEffect(() => {
    const onPageHide = () => {
      void releaseVisitLock();
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [releaseVisitLock]);

  // Lock and load on mount
  useEffect(() => {
    let cancelled = false;
    visitLockedBySessionRef.current = false;
    dispenseSavedRef.current = false;

    const ensureTeleConsultMeet = async () => {
      setTeleConsultMeetUrl(null);
      setTeleConsultMeetError(null);
      setTeleConsultMeetSkipped(false);
      setTeleConsultMeetLoading(true);
      const dashed = visitUlidToApiGuid(patient.visitULID);
      if (!dashed) {
        if (!cancelled) {
          setTeleConsultMeetError('Invalid visit id for Meet link.');
          setTeleConsultMeetLoading(false);
        }
        return;
      }
      try {
        const res = await apiService.generateConsultLink(dashed);
        if (cancelled) return;
        setTeleConsultMeetUrl(res.googleMeetUrl ?? null);
        setTeleConsultMeetSkipped(!!res.googleMeetSkipped);
      } catch (err: unknown) {
        if (cancelled) return;
        setTeleConsultMeetError(getApiErrorMessage(err, 'Could not load Google Meet link. Please try again.'));
      } finally {
        if (!cancelled) setTeleConsultMeetLoading(false);
      }
    };

    const lockAndLoad = async () => {
      setTeleConsultMeetUrl(null);
      setTeleConsultMeetError(null);
      setTeleConsultMeetSkipped(false);
      setTeleConsultMeetLoading(false);
      setMeetLiveStatus(null);
      try {
        // Staff dispense is co-operative (view orders/chat context) — do not take an exclusive lock
        // that blocks the assigned doctor from saving the case note or dispense.
        if (!isStaffPortal) {
          const lockResult = await apiService.lockVisit(patient.visitULID);
          if (!lockResult) {
            const isLocked = await apiService.getVisitLockStatus(patient.visitULID);
            if (isLocked) {
              setAlertModal({ show: true, message: 'Visit is currently locked by another user. Please wait or contact the user who has it open.', type: 'error' });
              setTimeout(() => { handleBack(); }, 2000);
              return;
            }
          }
          visitLockedBySessionRef.current = true;
        }
        const details = await loadPatientDetails();
        loadCurrentOrders();
        loadCurrentCaseNote();
        if (!cancelled && details?.teleMed) {
          await ensureTeleConsultMeet();
        }
      } catch (error: any) {
        console.error('Error locking visit:', error);
        if (error.response?.status === 409 || error.response?.data?.errorCode === 'VISIT_LOCKED') {
          setAlertModal({ show: true, message: 'Visit is currently locked by another user. Please wait or contact the user who has it open.', type: 'error' });
          setTimeout(() => { handleBack(); }, 2000);
        }
      }
    };
    void lockAndLoad();
    return () => {
      cancelled = true;
      void releaseVisitLock();
    };
  }, [patient.visitULID, releaseVisitLock, handleBack, isStaffPortal]);

  // Poll Meet API v2 live status for tele visits (occupancy / participant count).
  useEffect(() => {
    if (!patientDetails.teleMed) {
      setMeetLiveStatus(null);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await apiService.getTeleVisitMeetStatus(patient.visitULID);
        if (!cancelled) setMeetLiveStatus(s);
      } catch {
        if (!cancelled) setMeetLiveStatus(null);
      }
    };
    void tick();
    const id = window.setInterval(tick, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [patient.visitULID, patientDetails.teleMed]);

  // Load history data when page changes
  const loadHistoryForPage = useCallback((currentPage: number) => {
    if (currentPage === 0 && caseNoteHistory.length === 0) {
      loadCaseNoteHistory();
    } else if (currentPage === 2 && dispenseHistory.length === 0) {
      loadDispenseHistory();
    } else if (currentPage === 3 && memos.length === 0) {
      loadMemos();
    } else if (currentPage === 4 && mcHistory.length === 0) {
      loadMCHistory();
    }
  }, [caseNoteHistory.length, dispenseHistory.length, memos.length, mcHistory.length]);

  // Populate memo form when editing
  useEffect(() => {
    if (editingMemo) {
      setMemoForm({
        recipient: editingMemo.recipient || '',
        subHeader: editingMemo.subHeader || '',
        body: editingMemo.body || '',
        signOff: editingMemo.signOff || '',
        memoDate: editingMemo.memoDate ? toDateOnlyISO(editingMemo.memoDate) : getTodayISO()
      });
    } else {
      setMemoForm(DEFAULT_MEMO_FORM);
    }
  }, [editingMemo]);

  // Item modal safety check
  useEffect(() => {
    if (showItemModal && patientDetails.storeId) {
      if (!(patientDetails.empId != null && patientDetails.empId > 0)) {
        setShowItemModal(false);
        setAlertModal({ show: true, message: 'Please pick the patient first before adding items.', type: 'error' });
        return;
      }
      searchItems(itemSearch, itemFilterRef.current);
    }
  }, [showItemModal, patientDetails.storeId, patientDetails.empId, itemFilter]);

  return {
    // Patient
    patientDetails, patientDetailsLoading,
    teleConsultMeetUrl, teleConsultMeetLoading, teleConsultMeetError, teleConsultMeetSkipped,
    meetLiveStatus,
    // Form
    caseNote, setCaseNote, savedCaseNote, setSavedCaseNote, caseNoteSaved, setCaseNoteSaved,
    orders, setOrders, ordersLoading,
    // Modals
    showMCModal, setShowMCModal,
    showItemModal, setShowItemModal,
    showDiagnosisModal, setShowDiagnosisModal,
    showOrderPanel, setShowOrderPanel,
    editingOrder, setEditingOrder,
    isAddingNewItem, setIsAddingNewItem,
    showDrugAllergyModal, setShowDrugAllergyModal,
    showDrugAllergyDetailModal, setShowDrugAllergyDetailModal,
    showMemoModal, setShowMemoModal,
    showMemoTypeModal, setShowMemoTypeModal,
    // Drug allergy
    drugAllergyValidation, drugAllergyDetails, drugAllergyLoading,
    drugList, selectedDrug, setSelectedDrug, drugSearchTerm, setDrugSearchTerm,
    editingDrugAllergy, setEditingDrugAllergy,
    isAddingNewDrugAllergy, setIsAddingNewDrugAllergy,
    drugAllergyForm, setDrugAllergyForm,
    drugProbabilityList, informationSourceList, drugAllergyIndicatorList,
    alertIndicatorList, reactionOutcomeList, adverseReactionList,
    resetDrugAllergyForm,
    handleOpenDrugAllergyModal, handleSelectDrug, handleEditDrugAllergy,
    handleSaveDrugAllergy, handleSaveNoDrugAllergy, handleRemoveDrugAllergy,
    handleDrugSearchChange,
    // Alert/Confirm
    alertModal, setAlertModal, confirmModal, setConfirmModal,
    // MC
    mcDays, setMcDays, mcFromDate, setMcFromDate, mcDateError, setMcDateError,
    mcRemarks, setMcRemarks, editingMC, setEditingMC,
    // Item search
    itemSearch, setItemSearch, itemFilter, setItemFilter,
    items, filteredItems, itemsLoading,
    // History
    caseNoteHistory, dispenseHistory, mcHistory, historyLoading,
    // Memos
    memos, memoLoading, editingMemo, setEditingMemo,
    selectedMemoType, setSelectedMemoType, memoForm, setMemoForm,
    // Diagnosis
    selectedAcute, setSelectedAcute, selectedChronic, setSelectedChronic,
    // HSS
    hssSelection, setHssSelection,
    diagnosisSearch, setDiagnosisSearch, diagnosisTab, setDiagnosisTab,
    expandedCategories, toggleAcute, toggleChronic, toggleCategory, filterDiagnoses,
    // Sidebar
    sidebarOpen, setSidebarOpen,
    // Actions
    handleBack, handlePickButtonClick, getPickButtonState,
    addItem, removeItem, editOrder, saveEditedOrder, totalAmount,
    handleSaveClick, handleConfirmSave, handleFinishConsult,
    searchItems, loadMemos, loadCurrentOrders, loadMCHistory,
    isCheckedOut, isExternalConsult: isExternalConsult || false,
    isStaffPortal,
    // Page loading
    loadHistoryForPage,
  };
}
